import {
  setItem as consentSetItem,
  getItem as consentGetItem,
  removeItem as consentRemoveItem,
  ConsentGroup,
  ConsentError,
} from '@indeed/storage-consent-management'
import { datadogRum } from '@datadog/browser-rum-slim'

// Re-export consent-related types
export { ConsentGroup, ConsentError }

export interface SetItemOptions {
  consentGroup?: ConsentGroup
  onConsentError?: (error: ConsentError) => void
}

/**
 * Proxy localStorage.getItem through consent management.
 * Returns null if localStorage is inaccessible.
 */
export function getItem(key: string): string | null {
  try {
    return consentGetItem(key)
  } catch (e) {
    return null
  }
}

/**
 * Set item in localStorage through consent management.
 * All consent groups go through the consent library.
 */
export function setItem(key: string, value: string | boolean, options?: SetItemOptions): void {
  const stringValue = typeof value === 'boolean' ? String(value) : value

  const consentGroup = options?.consentGroup ?? ConsentGroup.Functional
  const onConsentError = options?.onConsentError

  try {
    consentSetItem(key, stringValue, consentGroup)
  } catch (e) {
    if (e instanceof ConsentError) {
      datadogRum.addAction('storage.persist.failed', {
        storageKey: key,
        consentGroup,
        errorMessage: e.message,
      })
      // Invoke callback if provided
      if (onConsentError) {
        onConsentError(e)
      }
      // Do NOT propagate (error suppression for backward compat)
    }
    // Non-consent errors silently swallowed
  }
}

/**
 * Remove item from localStorage through consent management.
 * Errors are silently swallowed (preserves current behavior).
 */
export function removeItem(key: string): void {
  try {
    consentRemoveItem(key)
  } catch (e) {
    // Silently swallow errors
  }
}

/**
 * Parse JSON from localStorage item.
 * Returns null if the key does not exist or if JSON parsing fails.
 */
export function parseItem(key: string): any {
  try {
    const item = getItem(key)
    return item ? JSON.parse(item) : null
  } catch (e) {
    // Silently swallow parse errors
    return null
  }
}
